import React, { ReactNode, useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { ApiQueryTemplate } from '../../api';
import { FormikState } from 'formik/dist/types';
import {
  IconColor,
  SalvButton,
  SalvModal,
  SalvSwitch,
  SalvTagsInput,
  SalvTextArea,
  SalvTextField,
  Size,
} from '@salvapp/ui-components';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { useDispatch } from 'react-redux';
import { useCreateQueryTemplate, useDeleteQueryTemplate, useUpdateQueryTemplate } from './queryTemplatesActions';
import Has from '../../auth/Has';
import { CAN_ADD_QUERY_TEMPLATE, CAN_DELETE_QUERY_TEMPLATE, CAN_MODIFY_QUERY_TEMPLATE } from '../../auth/permissions';
import { useMe } from '../../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { commaSeparatedStringsRegex, contentToList } from './QueryTemplates';

const validationSchema = Yup.object().shape(
  {
    id: Yup.number(),
    name: Yup.string()
      .matches(/^\w+$/i, {
        message: 'Template name must contain letters, numbers or underscores',
      })
      .required(''),
    content: Yup.string().required('Required'),
    editAsList: Yup.boolean(),
  },
  []
);

export interface QueryTemplatesModalFormValues {
  id?: number;
  name: string;
  content: string;
  editAsList: boolean;
}

interface QueryTemplateModalProps {
  queryTemplate: ApiQueryTemplate | null;
  buttonLabel: ReactNode;
  title: ReactNode;
}

export const QueryTemplatesModal = ({ queryTemplate, buttonLabel, title }: QueryTemplateModalProps) => {
  const initialValues =
    queryTemplate !== null
      ? {
          ...queryTemplate,
          editAsList: commaSeparatedStringsRegex.test(queryTemplate.content),
        }
      : {
          name: '',
          content: '',
          editAsList: true,
        };

  const initialFormikState: Partial<FormikState<QueryTemplatesModalFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();

  const { mutate: createQueryTemplate } = useCreateQueryTemplate();
  const { mutate: deleteQueryTemplate } = useDeleteQueryTemplate();
  const { mutate: updateQueryTemplate } = useUpdateQueryTemplate();
  const { features } = useMe();
  const allowEditAsLists = isFeatureEnabled(ORGANISATION_FEATURE.EDIT_QUERY_TEMPLATES_AS_LISTS, features);

  const onDeleteQueryTemplate = async (queryTemplate: ApiQueryTemplate) => {
    if (queryTemplate.id) {
      dispatch(
        showConfirmationModal({
          title: 'Are you sure you want to delete this query template?',
          confirmationText: 'Yes, delete',
          cancelText: 'No',
          iconColor: IconColor.DANGER,
          confirmButtonVariant: 'danger',
          onConfirm: async () => {
            deleteQueryTemplate({ id: queryTemplate.id! });
            setShowModal(false);
          },
        })
      );
    }
  };

  const submit = async (
    values: QueryTemplatesModalFormValues,
    { setSubmitting, resetForm }: FormikHelpers<QueryTemplatesModalFormValues>
  ) => {
    try {
      if (!queryTemplate?.id) {
        createQueryTemplate({
          name: values.name,
          content: values.content,
        });
      } else {
        updateQueryTemplate({
          id: queryTemplate.id!,
          name: values.name,
          content: values.content,
        });
      }
    } finally {
      setShowModal(false);
      setSubmitting(false);
      resetForm(initialFormikState);
    }
  };
  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={submit}
      validationSchema={validationSchema}
    >
      {({
        resetForm,
        values,
        setFieldValue,
        errors,
        isSubmitting,
        touched,
      }: FormikProps<QueryTemplatesModalFormValues>) => {
        const onClose = () => {
          setShowModal(false);
          resetForm();
        };

        const updateContentFromList = (value: string[]) =>
          setFieldValue(
            'content',
            value
              .map((item) => item.trim())
              .filter((item) => item)
              .map((item) => `'${item}'`)
              .join(',')
          );

        return (
          <React.Fragment>
            <SalvButton label={buttonLabel} onClick={() => setShowModal(true)} size={Size.SMALL} variant='muted' />
            <SalvModal show={showModal} onHide={() => onClose()}>
              <Form>
                <SalvModal.Header>{title}</SalvModal.Header>
                <SalvModal.Body>
                  <div className='m-b-16'>
                    <SalvTextField
                      label='Name'
                      value={values.name}
                      onChange={(value: string) => setFieldValue('name', value)}
                      isInvalid={!!errors.name}
                    />
                    {errors.name && <div className={'salv-error-message'}>{errors.name}</div>}
                  </div>
                  <div className='m-b-16'>
                    {(!allowEditAsLists || !values.editAsList) && (
                      <SalvTextArea
                        label='Content'
                        value={values.content}
                        onChange={(value: string) => setFieldValue('content', value)}
                        isInvalid={!!errors.content && touched.content}
                        autoSizing={true}
                      />
                    )}
                    {allowEditAsLists && values.editAsList && (
                      <SalvTagsInput
                        label='Content'
                        value={contentToList(values.content)}
                        onChange={(value: string[]) => updateContentFromList(value)}
                        isInvalid={!!errors.content && touched.content}
                        placeholder={'Enter a new value'}
                      />
                    )}
                    {errors.content && touched.content && <div className={'salv-error-message'}>{errors.content}</div>}
                  </div>
                  {allowEditAsLists && (
                    <div className='m-b-16'>
                      <SalvSwitch
                        label='Edit as list'
                        selected={values.editAsList}
                        onChange={(newEditAsList) => {
                          setFieldValue('editAsList', newEditAsList);
                        }}
                      />
                    </div>
                  )}
                </SalvModal.Body>

                <SalvModal.Footer>
                  {queryTemplate && (
                    <Has allPermissions={CAN_DELETE_QUERY_TEMPLATE}>
                      <SalvButton
                        disabled={isSubmitting}
                        onClick={() => onDeleteQueryTemplate(queryTemplate)}
                        variant='danger'
                        size={Size.LARGE}
                        label='Delete'
                        className='me-auto'
                      />
                    </Has>
                  )}
                  <SalvButton size={Size.LARGE} variant='muted' onClick={onClose} label='Close' />

                  <Has allPermissions={[CAN_ADD_QUERY_TEMPLATE, CAN_MODIFY_QUERY_TEMPLATE]}>
                    <SalvButton
                      size={Size.LARGE}
                      variant='primary'
                      disabled={isSubmitting}
                      type='submit'
                      label='Save'
                    />
                  </Has>
                </SalvModal.Footer>
              </Form>
            </SalvModal>
          </React.Fragment>
        );
      }}
    </Formik>
  );
};

export default QueryTemplatesModal;
